import { useState } from 'react'

import { SecondarySmallButton } from '../../../components/ReusableComponents/Button/SmallButtons/index'
import ConfirmationDialog from '../../../components/ReusableComponents/ConfirmationDialog/index'
import TextInput from '../../../components/ReusableComponents/ManageHotel/TextInput/index'
import Toggle from '../../../components/ReusableComponents/Toggle/index'
import styles from './EditTASettingsDialog.module.scss'

// Sentinel value persisted to the backend that means "no rating qualifies for an
// external TA redirect", which effectively keeps every review inside Directful.
const DISABLED_MIN_RATING = 10

// Used as the editable value when the dialog opens already in the disabled state
// and there is no remembered previous rating to restore.
const FALLBACK_PREVIOUS_RATING = 5

const PREVIOUS_RATING_STORAGE_PREFIX = 'reviewTripAdvisorPreviousMinStars:'

// Guests can only choose whole 1-5 stars on the public review page, so the
// preview enumerates the same set.
const PREVIEW_RATINGS = [1, 2, 3, 4, 5]

const isRatingDisabled = value => Number(value) >= DISABLED_MIN_RATING

const isEditableRatingValid = value =>
  value <= 5 && value >= 0.5 && value % 0.5 === 0

const readPreviousRatingFromStorage = subsidiaryId => {
  if (!subsidiaryId) return null
  try {
    const stored = window.localStorage.getItem(
      PREVIOUS_RATING_STORAGE_PREFIX + subsidiaryId
    )
    return stored != null && stored !== '' ? Number(stored) : null
  } catch {
    return null
  }
}

const writePreviousRatingToStorage = (subsidiaryId, value) => {
  if (!subsidiaryId) return
  try {
    window.localStorage.setItem(
      PREVIOUS_RATING_STORAGE_PREFIX + subsidiaryId,
      String(value)
    )
  } catch {
    // localStorage may be unavailable (private mode / quota); fail silently.
  }
}

// Mirrors the routing logic in www-savibook Review/index.js: the guest is sent
// to the external review provider only when their rating reaches the threshold.
const goesToExternalReview = (rating, isDisabled, threshold) => {
  if (isDisabled) return false
  return rating >= Number(threshold)
}

const EditTASettingsDialog = ({
  subsidiaryId,
  reviewTripAdvisorMinStars,
  handleClose,
  handleConfirm
}) => {
  const initiallyDisabled = isRatingDisabled(reviewTripAdvisorMinStars)
  const initialEditableRating = initiallyDisabled
    ? readPreviousRatingFromStorage(subsidiaryId) ?? FALLBACK_PREVIOUS_RATING
    : reviewTripAdvisorMinStars

  const [isDisabled, setIsDisabled] = useState(initiallyDisabled)
  const [minRating, setMinRating] = useState(initialEditableRating)
  const [isPreviewVisible, setIsPreviewVisible] = useState(false)

  function handleToggleDisable(e) {
    setIsDisabled(e.target.checked)
  }

  const isSaveDisabled = !isDisabled && !isEditableRatingValid(minRating)

  const previewRatingValid = isDisabled || isEditableRatingValid(minRating)

  return (
    <ConfirmationDialog
      title="Review settings"
      theme="blacklist"
      positiveMessage="Save changes"
      onClose={handleClose}
      onConfirm={() => {
        // Always remember the editable rating so re-enabling later restores the
        // value the user had before disabling, even after a page reload.
        writePreviousRatingToStorage(subsidiaryId, minRating)

        handleConfirm({
          reviewTripAdvisorMinStars: isDisabled
            ? DISABLED_MIN_RATING
            : Number(minRating)
        })
      }}
      isButtonDisabled={isSaveDisabled}
    >
      <div className={styles.container}>
        <TextInput
          label=" Min. rating for TA review"
          value={minRating}
          type="number"
          step={0.5}
          max={5}
          min={0.5}
          disabled={isDisabled}
          onChange={e => setMinRating(e.target.value)}
        />
        <div className={styles.disableRow}>
          <Toggle
            label="Disable"
            checked={isDisabled}
            handleChange={handleToggleDisable}
          />
          <SecondarySmallButton
            type="button"
            className={styles.testButton}
            disabled={!previewRatingValid}
            onClick={() => setIsPreviewVisible(prev => !prev)}
          >
            {isPreviewVisible ? 'Hide test' : 'Test'}
          </SecondarySmallButton>
        </div>
        {isDisabled && (
          <span className={styles.disableHint}>
            All reviews will stay in Directful. No external review link will be sent.
          </span>
        )}
        {isPreviewVisible && previewRatingValid && (
          <ReviewRoutingPreview
            isDisabled={isDisabled}
            minRating={Number(minRating)}
          />
        )}
      </div>
    </ConfirmationDialog>
  )
}

const ReviewRoutingPreview = ({ isDisabled, minRating }) => (
  <div className={styles.previewPanel}>
    <div className={styles.previewHeader}>
      <span className={styles.previewTitle}>Review flow preview</span>
      <span className={styles.previewSubtitle}>
        {isDisabled
          ? 'External review link is disabled — every guest stays in Directful.'
          : `Guests rating ${minRating}\u2605 or higher will be sent to the external review page. Lower ratings stay in Directful.`}
      </span>
    </div>
    <ul className={styles.previewList}>
      {PREVIEW_RATINGS.map(rating => {
        const external = goesToExternalReview(rating, isDisabled, minRating)
        return (
          <li key={rating} className={styles.previewRow}>
            <span className={styles.previewRating}>
              {Array.from({ length: rating }).map((_, i) => (
                <span key={`f-${i}`} className={styles.previewStarFilled}>★</span>
              ))}
              {Array.from({ length: 5 - rating }).map((_, i) => (
                <span key={`e-${i}`} className={styles.previewStarEmpty}>★</span>
              ))}
            </span>
            <span
              className={[
                styles.previewOutcome,
                external ? styles.previewOutcomeExternal : styles.previewOutcomeInternal
              ].join(' ')}
            >
              {external
                ? 'External review (TripAdvisor link)'
                : 'Stays in Directful (private feedback)'}
            </span>
          </li>
        )
      })}
    </ul>
  </div>
)

export default EditTASettingsDialog
